import type {
    PartnerCockpitContactDetail,
    PartnerCockpitLeadStatus,
    PartnerCockpitOfferRequestContact,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import { EngagementContactRequestType } from '@houzy/schemas/partner-cockpit/partner-matching';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { contactOptionRequiresPrice, offerRequestContactOptionLabel } from '../labels';
import type { ContactRequestValue, OfferRequestContactOption } from '../types';
import { ContactOptionsForm } from './ContactOptionsForm';
import { ContactRequestForm } from './ContactRequestForm';
import { ContactViaPhoneEmailDialog } from './ContactViaPhoneEmailDialog';
import { OfferRequestContactCard } from './OfferRequestContactCard';
import { ShowInterestDialog } from './ShowInterestDialog';

export type OfferRequestContactPanelProps = {
    contact: PartnerCockpitOfferRequestContact;
    leadStatus: PartnerCockpitLeadStatus;
    contactRestricted: boolean;
    onRevealContactDetail: (detail: PartnerCockpitContactDetail) => void;
    isOnOldContract?: boolean;
    fillHeight?: boolean;
    submitDisabled?: boolean;
    onSubmitRequest?: (option: OfferRequestContactOption, value: ContactRequestValue) => void;
    onConfirmInterest?: (option: OfferRequestContactOption) => void;
    onConfirmPhoneEmailContact?: (option: OfferRequestContactOption) => Promise<unknown> | void;
    onPhoneEmailContactCompleted?: () => void;
};

export function OfferRequestContactPanel({
    contact,
    leadStatus,
    contactRestricted,
    onRevealContactDetail,
    isOnOldContract,
    fillHeight = false,
    submitDisabled = false,
    onSubmitRequest,
    onConfirmInterest,
    onConfirmPhoneEmailContact,
    onPhoneEmailContactCompleted,
}: OfferRequestContactPanelProps): ReactElement {
    const { t } = useTranslation();
    const [selectedOption, setSelectedOption] = useState<OfferRequestContactOption>();
    const [interestDialogOpen, setInterestDialogOpen] = useState(false);
    const [phoneEmailDialog, setPhoneEmailDialog] = useState<'closed' | 'confirm' | 'revealed'>('closed');
    const [revealPending, setRevealPending] = useState(false);

    function handleSelect(option: OfferRequestContactOption): void {
        if (option === 'ShowInterest') {
            setInterestDialogOpen(true);
            return;
        }
        if (option === EngagementContactRequestType.ContactViaPhoneOrEmail) {
            setPhoneEmailDialog('confirm');
            return;
        }
        setSelectedOption(option);
    }

    function resetToOptions(): void {
        setSelectedOption(undefined);
    }

    function handleConfirmInterest(): void {
        setInterestDialogOpen(false);
        onConfirmInterest?.('ShowInterest');
    }

    async function handleConfirmPhoneEmail(): Promise<void> {
        setRevealPending(true);
        try {
            await onConfirmPhoneEmailContact?.(EngagementContactRequestType.ContactViaPhoneOrEmail);
            setPhoneEmailDialog('revealed');
        } catch {
            // The page surfaces the failure; without the customer's consent there is nothing to reveal.
            setPhoneEmailDialog('closed');
        } finally {
            setRevealPending(false);
        }
    }

    function handleSubmitRequest(value: ContactRequestValue): void {
        if (selectedOption === undefined || submitDisabled) {
            return;
        }
        onSubmitRequest?.(selectedOption, value);
        resetToOptions();
    }

    const showRequestForm =
        selectedOption !== undefined &&
        (selectedOption === 'ShowInterestWithMessage' || contactOptionRequiresPrice(selectedOption));

    return (
        <>
            <OfferRequestContactCard
                contact={contact}
                leadStatus={leadStatus}
                contactRestricted={contactRestricted}
                isOnOldContract={isOnOldContract}
                fillHeight={fillHeight}
                onRevealContactDetail={onRevealContactDetail}>
                {showRequestForm ? (
                    <ContactRequestForm
                        title={offerRequestContactOptionLabel(t, selectedOption)}
                        requiresPrice={contactOptionRequiresPrice(selectedOption)}
                        onSubmit={handleSubmitRequest}
                        onCancel={resetToOptions}
                    />
                ) : (
                    <ContactOptionsForm
                        value={selectedOption}
                        isOnOldContract={isOnOldContract}
                        onSelect={handleSelect}
                    />
                )}
            </OfferRequestContactCard>

            <ShowInterestDialog
                open={interestDialogOpen}
                onConfirm={handleConfirmInterest}
                onCancel={() => setInterestDialogOpen(false)}
            />

            <ContactViaPhoneEmailDialog
                open={phoneEmailDialog !== 'closed'}
                step={phoneEmailDialog === 'revealed' ? 'revealed' : 'confirm'}
                contact={contact}
                confirmDisabled={revealPending}
                onConfirm={() => void handleConfirmPhoneEmail()}
                onCancel={() => setPhoneEmailDialog('closed')}
                onDone={() => {
                    setPhoneEmailDialog('closed');
                    onPhoneEmailContactCompleted?.();
                }}
            />
        </>
    );
}
